<template>
  <div>
    <div
      v-for="(productCategory, index) in visibleProductCategories"
      :key="productCategory.id"
      class="event-products--edits mb-3"
      :data-scroll-focus="`product-${productCategory.id}`"
    >
      <v-card
        v-edit-vertical-line="{
          index: index,
          targetSelector: '.event-products--edits'
        }"
        color="secondary"
        class="fipa--header"
        tile
      >
        <v-row no-gutters align="center">
          <v-col>
            <div class="fipa--header-title">
              {{ productCategory.name }}
            </div>
          </v-col>
          <v-col cols="3" class="text-right">
            <v-btn
              v-if="state.canAdd"
              class="btn-with-icon"
              variant="text"
              data-test="productAdd"
              :to="actions.addItem(productCategory.id)"
            >
              <div class="bg-edit btn-icon-with-text">
                <v-icon size="small" class="text-white"> $iconPlus </v-icon>
              </div>
              <span class="text-white">{{
                $t("common:button.add", "Add")
              }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <table class="entsys--table">
        <thead>
          <tr>
            <th class="text-left" style="width: 180px">
              {{
                $t(
                  "events:EventPage.products.ProductName.column",
                  "Name",
                  "Product name column in products section in event page"
                )
              }}
            </th>
            <th class="text-left pr-0" style="width: 17%">
              {{
                $t(
                  "events:EventPage.products.Description.column",
                  "Description",
                  "Description column in products section in event page"
                )
              }}
            </th>
            <th class="text-right pl-0" style="width: 200px">
              {{
                $t(
                  "events:EventPage.products.PricePerItem.column",
                  "Price per item",
                  "Price per item column in products section in event page"
                )
              }}
              <br />
              <span class="text-caption">
                {{
                  $t(
                    "events:EventPage.products.PricePerItemHint.column",
                    "including booking fee (if any)",
                    "Price column hint column in products section in event page"
                  )
                }}
              </span>
            </th>
            <th>
              {{ $t("events:EventPage.products.Quantity.column", "Quantity") }}
            </th>
            <th class="text-left" style="width: 420px">
              {{
                $t(
                  "events:EventPage.products.Conditions.column",
                  "Payment conditions",
                  "Payment conditions column in products section in event page"
                )
              }}
            </th>
            <th v-if="state.canAdd" colspan="2" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="eventProduct in actions.filteredEventProducts(
              productCategory.id
            )"
            :key="eventProduct.id"
            :data-test="`product-${eventProduct.name}`"
            :data-scroll-focus="`eventProduct-${eventProduct.id}`"
          >
            <td>
              <div>
                {{ eventProduct.name }}
                <AppShowLockState
                  v-if="actions.eventProductLocked(eventProduct)"
                  lock-state
                  class="ml-1"
                />
              </div>
              <div>{{ eventProduct.refCode }}</div>
            </td>
            <td class="pr-0">
              <span
                class="force-paragraph-no-margin"
                v-html="eventProduct.description"
              />
            </td>
            <td class="text-right pl-0">
              <span
                v-if="
                  eventProduct.paymentStructure ==
                  'full_price_online_booking_nonrefundable'
                "
              >
                <span class="text-accent bold currency-display">
                  {{ actions.masterCurrency(eventProduct.priceCents) }}
                </span>
                <br />
                <span class="mr-1">{{
                  $t(
                    "events:EventPage.products.BookingFee.hint",
                    "booking fee",
                    "Booking fee hint in products section in event page"
                  )
                }}</span>
                <span class="text-accent bold currency-display">
                  {{ actions.masterCurrency(eventProduct.bookingFeeCents) }}
                </span>
                <br />

                <span class="mr-1"
                  ><b>
                    {{
                      $t("events:EventPage.products.totalFee.label", "Total:")
                    }}
                  </b></span
                >
                <span class="text-accent bold currency-display">
                  {{
                    actions.masterCurrency(
                      eventProduct.priceCents + eventProduct.bookingFeeCents
                    )
                  }}
                </span>
              </span>
              <span v-else>
                <span class="text-accent bold currency-display">
                  {{ actions.masterCurrency(eventProduct.priceCents) }}
                </span>
              </span>
            </td>
            <td>
              <AppShowEventProductsPurchased
                :value="{
                  confirmedPurchasesCount: eventProduct.confirmedPurchasesCount,
                  limitPurchasesForPublicRegistration:
                    eventProduct.limitPurchasesForPublicRegistration
                }"
              />
            </td>
            <td>
              <ShowPaymentStructure v-bind="{ eventProduct }" />
            </td>
            <template v-if="state.canAdd">
              <td>
                <v-tooltip
                  v-if="eventProduct.eventProductCustomFields.length"
                  location="bottom"
                >
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-icon color="primary"> $iconClipboard </v-icon>
                    </div>
                  </template>
                  <span>{{
                    $t("events:EventPage.products.customFields.icon.text")
                  }}</span>
                </v-tooltip>
              </td>
              <td class="text-right actions-space">
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      eventProduct.allowedMutations,
                      'eventProductClone'
                    )
                  "
                  class="bg-edit ma-1"
                  dark
                  data-test="eventProductClone"
                  icon
                  size="small"
                  :aria-label="
                    $t('events:EventProduct.cloneEventProductButton.alt')
                  "
                  @click="actions.cloneItem(eventProduct, $event)"
                >
                  <v-icon size="small" class="text-white"> $iconCopy </v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="21"
                    indeterminate
                  />
                </v-btn-icon>

                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      eventProduct.allowedMutations,
                      'eventEventProductCreateOrUpdate'
                    )
                  "
                  data-test="eventEditProduct"
                  class="bg-edit-green ma-1"
                  :aria-label="$t('common:editButton.alt')"
                  icon
                  size="small"
                  :to="actions.editItem(eventProduct)"
                >
                  <v-icon color="white" size="small"> $edit </v-icon>
                </v-btn-icon>
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      eventProduct.allowedMutations,
                      'eventProductDelete'
                    )
                  "
                  class="bg-remove ma-1"
                  data-test="eventDeleteProduct"
                  :aria-label="$t('common:deleteButton.alt')"
                  icon
                  size="small"
                  @click="actions.deleteItem(eventProduct.id, $event)"
                >
                  <v-icon color="white" size="small"> $iconTrash </v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="21"
                    indeterminate
                  />
                </v-btn-icon>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import editVerticalLine from "@/directives/edit-vertical-line";

export default {
  name: "EventProductsDesktop",
  directives: { editVerticalLine }
};
</script>
<script setup>
import ShowPaymentStructure from "@elements/ShowPaymentStructure.vue";
import AppShowEventProductsPurchased from "@AppShows/event/AppShowEventProductsPurchased.vue";
import AppShowLockState from "@AppShows/event/AppShowLockState.vue";

defineProps({
  visibleProductCategories: {
    type: Array,
    required: true
  },
  state: {
    type: Object,
    required: true
  },
  actions: {
    type: Object,
    required: true
  }
});
</script>
